import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@configurator-messages'
import {
  Theme,
  Grid,
  Link,
  Box,
  Divider,
  DialogContent,
  Button,
  DialogTitle,
  Dialog,
} from '@material-ui/core'
import {
  customOligoRoute,
  cartRoute,
  milliplexRoute,
  CartView,
  CartType,
} from '@src/routes'
import {
  productData,
  Products,
  customCrisprProductData,
} from '@src/routes/Configurators/utils/productData'
import {
  Url,
  ProductTypes,
} from '@src/routes/SemiConfigurators/products/crispr/Enum'
import {
  GenomicProduct,
  GenomicsProperties,
} from '@src/routes/Configurators/products/Genomics/enum'
import { ProductEnum } from '@src/routes/Configurators/utils/MilliplexEnum'
import { ActiveLinkType } from '@src/routes/SemiConfigurators/products/crisprgrna/Enum'
import { useUserSession } from '@utils/useUserSession'
import { InvalidKeys } from '@src/routes/Configurators/utils/constants'
import { useCurrentUser } from '@utils/useCurrentUser'
import getConfig from 'next/config'

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    minHeight: theme.spacing(80),
    '& .MuiDialog-paperWidthMd': {
      width: '100%',
      maxWidth: theme.spacing(188),
      minHeight: theme.spacing(110),
      paddingBottom: theme.spacing(6),
    },
  },
  textAlign: {
    padding: theme.spacing(6.5, 0, 0, 3),
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(2),
  },

  card: {
    width: 200,
  },
  pos: {
    marginBottom: theme.spacing(3),
  },
  clearButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  AddModificationButton: {
    marginTop: theme.spacing(-3),
    minWidth: 180,
    color: theme.palette.primary.main,
    position: 'absolute',
    bottom: theme.spacing(-5),
  },
  bkColor: {
    backgroundColor: 'whitesmoke',
  },
  displayCard: {},
  nextButton: {
    marginTop: theme.spacing(5),
    marginRight: theme.spacing(2),
    minWidth: 220,
  },
  divMar: {
    margin: theme.spacing(5),
  },
  paddingGrid: {
    padding: theme.spacing(6, 0, 0, 6),
  },
  gridAlign: {
    paddingTop: theme.spacing(3),
  },
  subPadTop: {
    paddingTop: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  title: {},
  rootRow: {
    color: theme.palette.grey[500],
    fontWeight: theme.typography.fontWeightMedium,
  },
  mgLeft: {
    marginLeft: theme.spacing(3),
  },
  mgLeft5: {
    marginLeft: theme.spacing(1),
  },
  mgTop8: {
    marginTop: theme.spacing(8),
    wordBreak: 'break-word',
    paddingRight: theme.spacing(3),
  },
  gridPosition: {
    position: 'relative',
  },
  compProdDialog: {
    '& .MuiDialog-paperWidthMd': {
      width: '80%',
      maxWidth: theme.spacing(188),
      minHeight: theme.spacing(54),
    },
  },
  margin: {
    marginLeft: theme.spacing(-15),
  },

  overflow: {
    padding: theme.spacing(2, 6, 5),
  },
}))

interface AddToCartItemsProps {
  dialogOpen: boolean
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  price: string | JSX.Element
  totalItem: number
  companionProducts?: any[] | null | undefined
  productType: string
  urlProduct?: any | null | undefined
}

const AddToCartItems: React.FC<AddToCartItemsProps> = (props) => {
  const {
    publicRuntimeConfig: { featureFlags },
  } = getConfig()

  const classes = useStyles()
  const {
    dialogOpen,
    price,
    totalItem,
    companionProducts,
    productType,
    urlProduct,
  } = props
  const intl = useIntl()
  const {
    userSession: { language, country },
  } = useUserSession()
  const { hasOnlyBlueERP } = useCurrentUser()
  const companionProductData: Products[] = []
  if (companionProducts) {
    if (
      productType === GenomicProduct.CustomCrispr ||
      productType === ActiveLinkType.ProductType
    ) {
      companionProducts.forEach((item: string) => {
        const product: Products = {
          title: '',
          displayName: {
            id: InvalidKeys.KEY_NOT_PRESENT,
            defaultMessage: InvalidKeys.KEY_NOT_PRESENT,
          },
          description: {
            id: InvalidKeys.KEY_NOT_PRESENT,
            defaultMessage: InvalidKeys.KEY_NOT_PRESENT,
          },
          url: '',
          continueButtonUrl: '',
        }
        customCrisprProductData.forEach((obj) => {
          if (obj.title === item) {
            product.title = obj.title
            product.description = obj.description
            product.displayName = obj.displayName
            product.continueButtonUrl = obj.continueButtonUrl
            if (item === GenomicsProperties.ModifiedFNCAS9TRACR) {
              product.url = obj.url
            } else {
              product.url = `/${country}/${language}/${obj.url}`
            }
          }
        })
        if (product.title.length) {
          companionProductData.push(product)
        }
      })
    } else {
      companionProducts.forEach((item: string) => {
        const product: Products = {
          title: item,
          displayName: featureFlags.internalAndDualQuenchProbes
            ? messages.CONF_STANDARD_TUBES_PROBE_NEW
            : messages.CONF_DNA_OLIGOS_IN_TUBES_PROBE,
          description: messages.DNA_OLIGO_IN_TUBES_DESC,
          url: '',
          continueButtonUrl: '',
        }
        productData.forEach((obj) => {
          if (obj.title === item) {
            product.title = obj.title
            product.description = obj.description
            product.displayName = obj.displayName
            product.url = obj.url
            product.continueButtonUrl = obj.continueButtonUrl
          }
        })

        companionProductData.push(product)
      })
    }
  }

  const getContinueButtonURL = () => {
    if (productType === ActiveLinkType.ProductType) {
      return '/products/molecular-biology-and-functional-genomics/gene-editing-and-functional-genomics/crispr-gene-editing'
    } else {
      const data = productData.filter((item) => item.title === productType)
      return data && data.length
        ? data[0].continueButtonUrl
        : customOligoRoute.index()
    }
  }

  const renderCompanionProduct = () => {
    if (companionProductData)
      return companionProductData.map((data, index) => (
        <Grid item xs={4} key={index} className={classes.gridPosition}>
          <div className={classes.mgTop8}>
            <Grid container>
              <Grid item xs={10}>
                <h3>
                  <FormattedMessage
                    {...data.displayName}
                    values={{
                      registered: (
                        <sup>
                          <FormattedMessage {...messages.CONF_TRADEMARK} />
                        </sup>
                      ),
                    }}
                  />
                </h3>
              </Grid>
            </Grid>
            <Grid className={classes.subPadTop} container spacing={6}>
              <Grid item xs={10}>
                <div>
                  <FormattedMessage
                    {...data.description}
                    values={{
                      registered: (
                        <sup>
                          <FormattedMessage {...messages.CONF_TRADEMARK} />
                        </sup>
                      ),
                    }}
                  />
                </div>
              </Grid>
              <Grid item xs={5}>
                <Button
                  variant="outlined"
                  color="inherit"
                  className={classes.AddModificationButton}
                  onClick={() => {
                    if (data.url !== '') {
                      window.location.assign(data.url)
                    }
                  }}
                >
                  {data.title === GenomicsProperties.ModifiedFNCAS9TRACR ? (
                    <FormattedMessage {...messages.REQUEST_QUOTE} />
                  ) : (
                    <FormattedMessage {...messages.ORDER_TEXT} />
                  )}
                </Button>
              </Grid>
            </Grid>
          </div>
        </Grid>
      ))
    else {
      return ''
    }
  }

  const onCheckout = () => {
    window.location.assign(
      hasOnlyBlueERP
        ? cartRoute[CartView.Cart](CartType.BlueCart)
        : cartRoute[CartView.Cart]()
    )
  }

  return (
    <Box>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        aria-label=""
        open={dialogOpen}
        maxWidth="md"
        className={companionProducts ? classes.dialog : classes.compProdDialog}
        id="add-to-cart-modal"
        data-testid="add-to-cart-modal"
      >
        <DialogTitle className={classes.title}>
          <FormattedMessage {...messages.CONF_YOUR_SHOPPING_CART_UPDATE} />
        </DialogTitle>

        <DialogContent className={classes.overflow}>
          <Grid container spacing={1}>
            <Grid item xs={10}>
              <div>
                <strong>
                  <FormattedMessage {...messages.CART_SUMMARY} />:
                </strong>
                <label className={classes.mgLeft}>{totalItem}</label>
                <label className={classes.mgLeft5}>
                  {intl.formatMessage(messages.CONF_ITEM_ADDED_TO_SHOPPIN_CART)}
                </label>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div>
                <strong>
                  <FormattedMessage
                    id="CART_SUBTOTAL"
                    defaultMessage="Subtotal"
                  />
                  :
                </strong>
                <label className={classes.mgLeft}>{price}</label>
              </div>
            </Grid>
            {productType === ProductTypes.ProductName ||
            productType === GenomicProduct.CustomCrispr ? (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.nextButton}
                  onClick={() => {
                    window.location.assign(urlProduct)
                  }}
                >
                  <FormattedMessage {...messages.SEMI_CONF_ORDER_MORE_CLONES} />
                </Button>

                <Button
                  color="primary"
                  variant="contained"
                  className={classes.nextButton}
                  onClick={() => {
                    window.location.assign(Url.CrisprUrl)
                  }}
                >
                  <FormattedMessage
                    {...messages.SEMI_CONF_NEW_PRODUCTS_AND_CONTROLS}
                  />
                </Button>

                <Button
                  color="primary"
                  variant="contained"
                  className={classes.nextButton}
                  onClick={onCheckout}
                  id="checkout-button"
                  data-testid="checkout-button"
                >
                  <FormattedMessage {...messages.CONF_PROCEED_TO_CHECKOUT} />
                </Button>
              </>
            ) : (
              <>
                {productType === ProductTypes.Shrna && (
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.nextButton}
                    onClick={() => {
                      window.location.assign(
                        '/technical-documents/technical-article/genomics/advanced-gene-editing/shrna-controls'
                      )
                    }}
                    id="other-products-button"
                    data-testid="other-products-button"
                  >
                    Other products and controls
                  </Button>
                )}

                <Button
                  color="primary"
                  variant="contained"
                  className={classes.nextButton}
                  onClick={onCheckout}
                  id="checkout-button"
                  data-testid="checkout-button"
                >
                  <FormattedMessage {...messages.CONF_PROCEED_TO_CHECKOUT} />
                </Button>

                {productType === ProductEnum.Milliplex ? (
                  <div className={classes.textAlign}>
                    <Link href={milliplexRoute.index()}>
                      <h3>
                        <FormattedMessage
                          {...messages.CONF_CONTINUE_SHOPPING}
                        />
                      </h3>
                    </Link>
                  </div>
                ) : (
                  <div className={classes.textAlign}>
                    <Link href={getContinueButtonURL()}>
                      <h3>
                        <FormattedMessage
                          {...messages.CONF_CONTINUE_SHOPPING}
                        />
                      </h3>
                    </Link>
                  </div>
                )}
              </>
            )}
          </Grid>
          {companionProducts ? (
            <>
              <div>
                <Divider className={classes.divMar} />
              </div>

              <Grid>
                <div>
                  <h3>
                    {productType === ActiveLinkType.ProductType ||
                    productType === GenomicProduct.CustomCrispr ? (
                      <FormattedMessage
                        {...messages.COMPANION_PRODUCT_HEADER_crisprgrna}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.CONF_CUSTOMERS_PURCHASED}
                      />
                    )}
                  </h3>
                </div>
              </Grid>
              <Grid container>{renderCompanionProduct()}</Grid>
            </>
          ) : (
            ''
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AddToCartItems
